import { jsPDF } from 'jspdf';
import { formatMoney, formatQuoteDate } from './quotes';

const MARGIN = 20;
const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const BOTTOM_LIMIT = PAGE_HEIGHT - MARGIN;

// Renders from the invoice document alone — including the business details
// snapshotted onto it at issue — so a downloaded copy always matches what was
// sent, even if the business details have changed since.
export function downloadInvoice(invoice) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  let y = MARGIN;
  const business = invoice.business || {};

  const newPageIfNeeded = (needed) => {
    if (y + needed > BOTTOM_LIMIT) {
      doc.addPage();
      y = MARGIN;
    }
  };

  const write = (text, { size = 10, style = 'normal', gap = 5, color = '#000000' } = {}) => {
    doc.setFont('helvetica', style);
    doc.setFontSize(size);
    doc.setTextColor(color);
    doc.splitTextToSize(String(text), CONTENT_WIDTH).forEach((line) => {
      newPageIfNeeded(gap);
      doc.text(line, MARGIN, y);
      y += gap;
    });
  };

  const rule = () => {
    newPageIfNeeded(6);
    doc.setDrawColor(200);
    doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);
    y += 6;
  };

  const labelled = (label, value, { bold = false, size = 9 } = {}) => {
    newPageIfNeeded(6);
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(size);
    doc.setTextColor('#000000');
    const width = doc.getTextWidth(value);
    doc.setTextColor('#666666');
    doc.text(label, MARGIN, y);
    doc.setTextColor('#000000');
    doc.text(value, PAGE_WIDTH - MARGIN - width, y);
    y += size >= 12 ? 8 : 5.5;
  };

  // "INVOICE" has to be unmistakable — a client's accounts team scans for it.
  write('INVOICE', { size: 20, style: 'bold', gap: 9 });
  write(business.name || '', { size: 11, style: 'bold', gap: 6 });
  write((business.addressLines || []).filter(Boolean).join(', '), { size: 8, color: '#666666', gap: 4 });
  if (business.companyNumber) {
    write(`Company no. ${business.companyNumber}`, { size: 8, color: '#666666', gap: 4 });
  }
  y += 4;
  rule();

  labelled('Invoice number', invoice.reference || '', { bold: true });
  labelled('Billed to', invoice.clientName ? `${invoice.clientName} (${invoice.clientEmail})` : invoice.clientEmail);
  if (formatQuoteDate(invoice.issuedAt)) labelled('Issue date', formatQuoteDate(invoice.issuedAt));
  if (formatQuoteDate(invoice.dueAt)) labelled('Payment due', formatQuoteDate(invoice.dueAt));
  if (invoice.status === 'paid') {
    labelled('Status', formatQuoteDate(invoice.paidAt) ? `Paid ${formatQuoteDate(invoice.paidAt)}` : 'Paid');
  }

  y += 2;
  rule();

  write('Description', { size: 9, style: 'bold', gap: 7 });
  newPageIfNeeded(8);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor('#000000');
  const amount = formatMoney(invoice.amount);
  const amountWidth = doc.getTextWidth(amount);
  const lines = doc.splitTextToSize(invoice.description || '', CONTENT_WIDTH - amountWidth - 8);
  doc.text(lines, MARGIN, y);
  doc.text(amount, PAGE_WIDTH - MARGIN - amountWidth, y);
  y += lines.length * 5 + 4;
  if (invoice.quoteTitle) {
    write(`From quote: ${invoice.quoteTitle}`, { size: 8, color: '#888888', gap: 4 });
    y += 2;
  }

  rule();
  newPageIfNeeded(10);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  const total = formatMoney(invoice.amount);
  doc.text('Amount due', MARGIN, y);
  doc.text(total, PAGE_WIDTH - MARGIN - doc.getTextWidth(total), y);
  y += 12;

  const bank = business.bank || {};
  rule();
  write('How to pay', { size: 9, style: 'bold', gap: 6 });
  labelled('Account name', bank.accountName || '');
  labelled('Sort code', bank.sortCode || '');
  labelled('Account number', bank.accountNumber || '');
  labelled('Reference', invoice.reference || '');
  y += 2;
  write(
    'Please quote the invoice reference with your payment. If bank details you have been sent '
    + 'by email differ from those shown here, contact us before paying.',
    { size: 7.5, color: '#888888', gap: 4 },
  );

  doc.save(`${(invoice.reference || 'invoice').toLowerCase()}.pdf`);
}
